/**
 * Who is using the app right now.
 *
 * Deliberately thin: the server is the authority, this is a cache of its answer so that routes
 * can render without a flash. Nothing here grants access — every protected call is checked
 * server-side (08 §3).
 */

import { create } from 'zustand'

import { fetchIdentity, type Identity } from './api'

interface SessionState {
  identity: Identity | null
  status: 'idle' | 'loading' | 'ready' | 'error'
  load: () => Promise<void>
  clear: () => void
}

export const useSession = create<SessionState>((set) => ({
  identity: null,
  status: 'idle',
  load: async () => {
    set({ status: 'loading' })
    try {
      set({ identity: await fetchIdentity(), status: 'ready' })
    } catch {
      // An unreachable API is not the same as being signed out, but for the shell's purposes
      // both mean "show the anonymous view and let the next call report the real error".
      set({
        identity: {
          principal: 'anonymous',
          id: null,
          role: null,
          csrf_token: null,
          must_change_password: false,
        },
        status: 'error',
      })
    }
  },
  clear: () =>
    set({
      identity: {
        principal: 'anonymous',
        id: null,
        role: null,
        csrf_token: null,
        must_change_password: false,
      },
    }),
}))
